import { useCallback, useState } from 'react';
import {
  useGetCompanyMutation,
  useGetCompanyFeaturesMutation,
  usePostLoginMutation,
} from 'services/login';
import { useSnackbarForMessage } from 'providers/SnackbarProvider';
import config from 'data/config';
import { jwtDecode } from 'jwt-decode';

export function useBuildLoginURL() {
  return useCallback(({ hasWFHEnabled, token }) => {
    const wfhURL = `${config.wfhUiUrl}/dashboard/?jwt=${token}&loginType=${encodeURIComponent('Interactive Login')}`;
    const hubURL = `${config.hubSSOUrl}/?jwt=${token}&loginType=${encodeURIComponent('Interactive Login')}`;
    return hasWFHEnabled ? wfhURL : hubURL;
  }, []);
}

export function useUniversaLoginUser() {
  const [getCompany] = useGetCompanyMutation();
  const [getCompanyFeatures] = useGetCompanyFeaturesMutation();
  const [postLogin] = usePostLoginMutation();

  return useCallback(async (values) => {
    const { roles, siteId, token } = await postLogin({ body: values }).unwrap();
    const { companyId } = await getCompany({ token, siteId }).unwrap();
    const { isSelfServiceEnabled, hasWFHEnabled } = await getCompanyFeatures({
      token,
      companyId,
    }).unwrap();
    return { isSelfServiceEnabled, hasWFHEnabled, roles, token };
  }, []);
}

export function useFreeTrialLogin() {
  const [isBusy, setIsBusy] = useState(false);
  const { announceSnackbar } = useSnackbarForMessage();
  const [getCompany, { isLoading: companyLoading }] = useGetCompanyMutation();
  const [getCompanyFeatures, { isLoading: featuresLoading }] =
    useGetCompanyFeaturesMutation();

  const routeLogin = useCallback(async (token) => {
    const { site } = jwtDecode(token);
    try {
      setIsBusy(true);
      const { companyId } = await getCompany({ token, siteId: site }).unwrap();
      const { hasWFHEnabled } = await getCompanyFeatures({
        token,
        companyId,
      }).unwrap();

      let loginUrl = null;

      // -----------------------------------------------------------------

      // if login is successful navigate to the correct page and set the submit
      // button as busy while waiting for the page to navigate.
      if (hasWFHEnabled) {
        loginUrl = `${config.wfhUiUrl}/dashboard/?jwt=${token}`;
      } else {
        loginUrl = `${config.hubSSOUrl}/?jwt=${token}`;
      }
      console.log('loginUrl', loginUrl);

      // check if window is available and if so redirect to the login url
      if (typeof window !== 'undefined') {
        window.location.replace(loginUrl);
      }
    } catch (error) {
      const message =
        error?.message ?? 'There was an error logging in. Please try again.';
      announceSnackbar(message, 'error');
      setIsBusy(false);
    }
  }, []);

  return {
    routeLogin,
    isLoading: companyLoading || featuresLoading || isBusy,
  };
}
